import {
  BLACKOUT_CATEGORY,
  BUNDLED_EXPERIENCES_CATEGORY,
  CAMPAIGN_CATEGORY,
  DEAL_OPTIONS_INCLUSIONS_CATEGORY,
  DISCOUNTS_MARGIN_CATEGORY,
  EGS_CATEGORY,
  SETUP_CATEGORY,
  SURCHARGES_CATEGORY,
  TAXES_PAYMENTS_CATEGORY,
} from './checklistCategories';
import { isRowApproved as isChecklistRowApproved } from './manualOverridePersistence';
import type { ChecklistStatus } from './shared/checklistUtils';
import type { BlackoutRowView } from './tabs/blackoutDates/types';
import type { BundledExperienceRowView } from './tabs/bundledExperiences/types';
import type { CampaignRowView } from './tabs/campaign/types';
import type { DealOptionInclusionRowView } from './tabs/dealOptionsInclusions/types';
import type { DiscountsMarginRowView } from './tabs/discountsMargin/types';
import type { EgsCapacityRowView, EgsMarginRowView } from './tabs/egs/types';
import type { SetupRowView } from './tabs/setup/types';
import type { SurchargeCheckRowView, SurchargePeriodRowView } from './tabs/surcharges/types';
import type { TaxesPaymentsRowView } from './tabs/taxesPayments/types';
import type { ApprovalReportSummary, ManualOverrideState } from './types';

export function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

export function buildReportSummaries({
  blackoutRows,
  bundledExperienceRows,
  baseSurchargesRows,
  taxesPaymentsRows,
  capacityRows,
  campaignRows,
  discountsMarginRows,
  dealOptionsInclusionsRows,
  manualOverrides,
  marginRows,
  offerId,
  propertyId,
  setupRows,
  surchargeTableRows,
  vendorId,
}: {
  blackoutRows: Array<BlackoutRowView>;
  bundledExperienceRows: Array<BundledExperienceRowView>;
  baseSurchargesRows: Array<SurchargeCheckRowView>;
  taxesPaymentsRows: Array<TaxesPaymentsRowView>;
  capacityRows: Array<EgsCapacityRowView>;
  campaignRows: Array<CampaignRowView>;
  discountsMarginRows: Array<DiscountsMarginRowView>;
  dealOptionsInclusionsRows: Array<DealOptionInclusionRowView>;
  manualOverrides: ManualOverrideState;
  marginRows: Array<EgsMarginRowView>;
  offerId: string;
  propertyId?: string;
  setupRows: Array<SetupRowView>;
  surchargeTableRows: Array<SurchargePeriodRowView>;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const summaries: Array<ApprovalReportSummary> = [];

  const setupRowsRequiringApproval = setupRows.filter((row) => !isRowApproved(row, manualOverrides));
  if (setupRowsRequiringApproval.length > 0) {
    const setupRowCopy = `setup row${setupRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: SETUP_CATEGORY.id,
      categoryLabel: SETUP_CATEGORY.label,
      count: setupRowsRequiringApproval.length,
      description: `${setupRowsRequiringApproval.length} ${setupRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const campaignRowsRequiringApproval = campaignRows.filter((row) => !isRowApproved(row, manualOverrides));
  if (campaignRowsRequiringApproval.length > 0) {
    const campaignRowCopy = `campaign row${campaignRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: CAMPAIGN_CATEGORY.id,
      categoryLabel: CAMPAIGN_CATEGORY.label,
      count: campaignRowsRequiringApproval.length,
      description: `${campaignRowsRequiringApproval.length} ${campaignRowCopy} still need review.`,
      sourceLinks: [
        {
          href: 'https://aussiecommerce.atlassian.net/wiki/spaces/SO/pages/2335834126/Contracting+and+Assessment+Information+Chains+Hotel+Specific',
          label: 'Open Policy Bible',
        },
      ],
    });
  }

  const discountsMarginRowsRequiringApproval = discountsMarginRows.filter(
    (row) => !isRowApproved(row, manualOverrides),
  );
  if (discountsMarginRowsRequiringApproval.length > 0) {
    const discountsMarginRowCopy = `Discounts & Margin row${
      discountsMarginRowsRequiringApproval.length === 1 ? '' : 's'
    }`;
    summaries.push({
      categoryId: DISCOUNTS_MARGIN_CATEGORY.id,
      categoryLabel: DISCOUNTS_MARGIN_CATEGORY.label,
      count: discountsMarginRowsRequiringApproval.length,
      description: `${discountsMarginRowsRequiringApproval.length} ${discountsMarginRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const blackoutRowsRequiringApproval = blackoutRows.filter((row) => !isRowApproved(row, manualOverrides));
  const blackoutReviewCount = blackoutRowsRequiringApproval.length;
  if (blackoutReviewCount > 0) {
    const blackoutRowCopy = `blackout row${blackoutReviewCount === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: BLACKOUT_CATEGORY.id,
      categoryLabel: BLACKOUT_CATEGORY.label,
      count: blackoutReviewCount,
      description: `${blackoutReviewCount} ${blackoutRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const marginRowsRequiringApproval = marginRows.filter((row) => !isRowApproved(row, manualOverrides));
  const capacityRowsRequiringApproval = capacityRows.filter((row) => !isRowApproved(row, manualOverrides));
  const egsRowsRequiringApproval = [...marginRowsRequiringApproval, ...capacityRowsRequiringApproval];
  if (egsRowsRequiringApproval.length > 0) {
    const egsParts = [
      buildReviewCountCopy(marginRowsRequiringApproval.length, 'margin row'),
      buildReviewCountCopy(capacityRowsRequiringApproval.length, 'capacity row'),
    ].filter(Boolean);
    summaries.push({
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${joinReviewCountParts(egsParts)} still need review.`,
      sourceLinks: buildEgsSourceLinks({ capacityRows, propertyId, vendorId }),
    });
  }

  const dealRowsRequiringApproval = dealOptionsInclusionsRows.filter((row) => !isRowApproved(row, manualOverrides));
  if (dealRowsRequiringApproval.length > 0) {
    const dealRowCopy = `inclusion row${dealRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: DEAL_OPTIONS_INCLUSIONS_CATEGORY.id,
      categoryLabel: DEAL_OPTIONS_INCLUSIONS_CATEGORY.label,
      count: dealRowsRequiringApproval.length,
      description: `${dealRowsRequiringApproval.length} ${dealRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const bundledExperienceRowsRequiringApproval = bundledExperienceRows.filter(
    (row) => !isRowApproved(row, manualOverrides),
  );
  if (bundledExperienceRowsRequiringApproval.length > 0) {
    const bundledExperienceRowCopy = `bundled experience row${
      bundledExperienceRowsRequiringApproval.length === 1 ? '' : 's'
    }`;
    summaries.push({
      categoryId: BUNDLED_EXPERIENCES_CATEGORY.id,
      categoryLabel: BUNDLED_EXPERIENCES_CATEGORY.label,
      count: bundledExperienceRowsRequiringApproval.length,
      description: `${bundledExperienceRowsRequiringApproval.length} ${bundledExperienceRowCopy} still need review.`,
      sourceLinks: [{ href: `/opportunity/${offerId}`, label: 'Verify source data' }],
    });
  }

  const taxesRowsRequiringApproval = taxesPaymentsRows.filter((row) => !isRowApproved(row, manualOverrides));
  if (taxesRowsRequiringApproval.length > 0) {
    const taxesRowCopy = `rule${taxesRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: TAXES_PAYMENTS_CATEGORY.id,
      categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
      count: taxesRowsRequiringApproval.length,
      description: `${taxesRowsRequiringApproval.length} ${taxesRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  const surchargeRowsRequiringApproval = [
    ...baseSurchargesRows.filter((row) => !isRowApproved(row, manualOverrides)),
    ...surchargeTableRows.filter((row) => !isRowApproved(row, manualOverrides)),
  ];
  if (surchargeRowsRequiringApproval.length > 0) {
    const surchargeRowCopy = `surcharge row${surchargeRowsRequiringApproval.length === 1 ? '' : 's'}`;
    summaries.push({
      categoryId: SURCHARGES_CATEGORY.id,
      categoryLabel: SURCHARGES_CATEGORY.label,
      count: surchargeRowsRequiringApproval.length,
      description: `${surchargeRowsRequiringApproval.length} ${surchargeRowCopy} still need review.`,
      sourceLinks: [],
    });
  }

  return summaries;
}

export function isRowApproved(row: { id: string; status: ChecklistStatus }, manualOverrides: ManualOverrideState) {
  return isChecklistRowApproved(row, manualOverrides);
}

export function getHiddenApprovedCount<Row extends { id: string; status: ChecklistStatus }>(
  rows: Array<Row>,
  manualOverrides: ManualOverrideState,
) {
  return rows.filter((row) => isRowApproved(row, manualOverrides)).length;
}

function buildReviewCountCopy(count: number, noun: string) {
  if (count === 0) {
    return '';
  }

  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function joinReviewCountParts(parts: Array<string>) {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityRowView>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}
